import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { ApolloQueryResult } from '@apollo/client/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataRequest } from '@tiltgroup/web-components';
import {
  Mutation,
  Query,
  QueryShipmentsArgs,
  Shipment,
  ShipmentDeleteInput,
  ShipmentPaginator,
} from '@graphql-types';
import { QueriesBaseService } from '@services/queries/queries-base.service';
import {
  deleteShipmentsMutation,
  importShipmentDocumentsMutation,
  ltlTrackingUrlQuery,
  restoreShipmentsMutation,
  shipmentQuery,
  shipmentsQuery,
} from '@services/queries/queries/shipments.queries';

@Injectable({
  providedIn: 'root',
})
export class ShipmentsQueriesService extends QueriesBaseService {
  private _apollo = inject(Apollo);

  getShipments(
    request: DataRequest<QueryShipmentsArgs>
  ): Observable<ShipmentPaginator> {
    const {
      page,
      size,
      where,
      trashed,
      hasTeam,
      createdBy,
      updatedBy,
      deletedBy,
      sort,
    } = request;

    return this._apollo
      .query({
        query: shipmentsQuery,
        variables: {
          page,
          size,
          where,
          trashed,
          hasTeam,
          createdBy,
          updatedBy,
          deletedBy,
          ...(sort && { orderBy: this.orderBy(sort) }),
        },
      })
      .pipe(map((res: ApolloQueryResult<Query>) => res?.data?.shipments));
  }

  getShipmentById(id: number): Observable<Shipment | null | undefined> {
    return this._apollo
      .query({
        query: shipmentQuery,
        variables: { id },
      })
      .pipe(map((res: ApolloQueryResult<Query>) => res?.data?.shipment));
  }

  /**
   * Carrier tracking URL for an LTL shipment (lighthouse-api ltlTracking query,
   * keyed on the shipment id). Typed inline since LtlTracking is not yet in the
   * generated GraphQL types.
   */
  getLtlTrackingUrl(shipmentId: number): Observable<string | null> {
    return this._apollo
      .query<{ ltlTracking: { trackingUrl: string | null } | null }>({
        query: ltlTrackingUrlQuery,
        variables: { id: shipmentId },
        fetchPolicy: 'no-cache',
      })
      .pipe(map((res) => res?.data?.ltlTracking?.trackingUrl ?? null));
  }

  importShipmentDocuments(attachments: File[]) {
    return this._apollo.mutate({
      mutation: importShipmentDocumentsMutation,
      variables: {
        attachments,
      },
      context: {
        useMultipart: true,
      },
    });
  }

  deleteShipments(ids: number[], input: ShipmentDeleteInput) {
    return this._apollo
      .mutate({
        mutation: deleteShipmentsMutation,
        variables: { ids, input },
      })
      .pipe(
        map((res: ApolloQueryResult<Mutation>) => res.data.deleteShipments)
      );
  }

  restoreShipments(ids: number[]) {
    return this._apollo
      .mutate({
        mutation: restoreShipmentsMutation,
        variables: { ids },
      })
      .pipe(
        map((res: ApolloQueryResult<Mutation>) => res.data.restoreShipments)
      );
  }
}
